<template>
  <Form
    ref="formValidate"
    class="agent-form"
    :model="formValidate"
    :rules="ruleValidate"
    :label-width="100"
    @submit.native.prevent="handleSubmit"
  >
    <FormItem label="代理商名称" prop="name" :error="fieldErrors.name">
      <Input
        ref="nameInput"
        v-model="formValidate.name"
        :maxlength="100"
        placeholder="请输入代理商名称"
        @on-change="clearFieldError('name')"
      />
    </FormItem>

    <FormItem label="负责人账号" prop="ownerUserId" :error="fieldErrors.ownerUserId">
      <Select
        ref="ownerSelect"
        v-model="formValidate.ownerUserId"
        filterable
        remote
        clearable
        transfer
        :loading="ownerLoading"
        :label="currentOwnerLabel"
        :remote-method="handleOwnerSearch"
        placeholder="按用户名或真实姓名搜索"
        @on-open-change="handleOwnerOpen"
        @on-query-change="handleOwnerQueryChange"
        @on-change="handleOwnerChange"
      >
        <Option
          v-for="item in displayedOwnerOptions"
          :key="item.id"
          :value="item.id"
          :label="getOwnerLabel(item)"
          :disabled="!item.status"
        >
          <div class="owner-option">
            <span class="owner-primary">{{ getOwnerLabel(item) }}</span>
            <span class="owner-secondary">
              {{ getOwnerSecondary(item) }}
              <em v-if="!item.status">账号已禁用</em>
            </span>
          </div>
        </Option>
      </Select>
      <div v-if="ownerTotal > ownerPageSize" class="owner-pagination">
        <span>共 {{ ownerTotal }} 个账号</span>
        <Page
          :current="ownerPage"
          :total="ownerTotal"
          :page-size="ownerPageSize"
          size="small"
          simple
          @on-change="handleOwnerPageChange"
        />
      </div>
      <Alert
        v-if="ownerSearched && !ownerLoading && !ownerTotal"
        class="owner-empty"
        type="warning"
        show-icon
      >
        未找到可用账号，请先到“帐号管理”创建账号
      </Alert>
    </FormItem>

    <FormItem label="上级代理商" prop="parentAgentId">
      <Select
        v-if="mode === 'add'"
        v-model="formValidate.parentAgentId"
        clearable filterable remote transfer
        :loading="parentLoading"
        :remote-method="searchParents"
        placeholder="留空为一级代理商，可搜索上级名称"
        @on-open-change="openParentOptions"
        @on-change="handleParentChange"
      >
        <Option v-for="item in displayedParentOptions" :key="item.id" :value="Number(item.id)" :disabled="item.status !== 1">
          {{ item.name }}（#{{ item.id }}{{ item.status !== 1 ? '，已禁用' : '' }}）
        </Option>
      </Select>
      <span v-else>{{ formValidate.parentAgentId ? `上级 #${formValidate.parentAgentId}` : '一级代理商' }}（请通过“调整上级”变更归属）</span>
      <Page v-if="mode === 'add' && parentTotal > 20" :current="parentPage" :total="parentTotal" :page-size="20" size="small" simple @on-change="changeParentPage" />
    </FormItem>

    <FormItem label="联系人" prop="contactName">
      <Input
        v-model="formValidate.contactName"
        :maxlength="100"
        placeholder="请输入联系人（选填）"
      />
    </FormItem>

    <FormItem label="联系电话" prop="phone">
      <Input
        ref="phoneInput"
        v-model="formValidate.phone"
        :maxlength="30"
        placeholder="请输入联系电话（选填）"
      />
    </FormItem>

    <FormItem label="状态" prop="status">
      <i-switch v-model="formValidate.status" :true-value="1" :false-value="0" size="large">
        <span slot="open">启用</span>
        <span slot="close">禁用</span>
      </i-switch>
    </FormItem>

    <FormItem class="form-actions">
      <Button :disabled="submitting" @click="handleReset">重置</Button>
      <Button
        type="primary"
        icon="md-checkmark"
        html-type="submit"
        :loading="submitting"
      >
        {{ mode === 'edit' ? '保存' : '创建' }}
      </Button>
    </FormItem>
  </Form>
</template>

<script>
import { buildAgentPayload, getAgentOwnerOptions, getAgentList } from '@/api/agent'

const createDefaultForm = () => ({
  name: '',
  ownerUserId: null,
  parentAgentId: null,
  contactName: '',
  phone: '',
  status: 1
})

const validateTrimmedRequired = (message) => (rule, value, callback) => {
  if (!String(value || '').trim()) callback(new Error(message))
  else callback()
}

const validatePhone = (rule, value, callback) => {
  const phone = String(value || '').trim()
  if (!phone) {
    callback()
    return
  }
  if (phone.length < 5 || phone.length > 30 || !/^[0-9+\-()\s]+$/.test(phone)) {
    callback(new Error('联系电话仅支持数字、空格、+、- 和括号，长度为 5 至 30 位'))
    return
  }
  callback()
}

export default {
  name: 'AgentForm',
  props: {
    mode: {
      type: String,
      default: 'add'
    },
    initialData: {
      type: Object,
      default: null
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      formValidate: createDefaultForm(),
      initialSnapshot: createDefaultForm(),
      currentOwnerOption: null,
      initialOwnerOption: null,
      ownerOptions: [],
      ownerLoading: false,
      ownerKeyword: '',
      ownerPage: 1,
      ownerPageSize: 20,
      ownerTotal: 0,
      ownerSearched: false,
      ownerSearchTimer: null,
      parentOptions: [],
      currentParentOption: null,
      parentLoading: false,
      parentKeyword: '',
      parentPage: 1,
      parentTotal: 0,
      parentRequestId: 0,
      parentSearchTimer: null,
      ownerRequestId: 0,
      fieldErrors: {
        name: '',
        ownerUserId: ''
      },
      ruleValidate: {
        name: [
          { required: true, validator: validateTrimmedRequired('请输入代理商名称'), trigger: 'blur' }
        ],
        ownerUserId: [
          { required: true, type: 'number', message: '请选择负责人账号', trigger: 'change' }
        ],
        phone: [
          { validator: validatePhone, trigger: 'blur' }
        ],
        status: [
          { required: true, type: 'number', message: '请选择正确的状态', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    displayedParentOptions () {
      const items = this.parentOptions.slice()
      if (this.currentParentOption && !items.some(item => Number(item.id) === Number(this.currentParentOption.id))) {
        items.unshift(this.currentParentOption)
      }
      return items
    },
    currentOwnerLabel () {
      return this.currentOwnerOption ? this.getOwnerLabel(this.currentOwnerOption) : ''
    },
    displayedOwnerOptions () {
      const options = this.ownerOptions.slice()
      if (this.currentOwnerOption && !options.some(item => item.id === this.currentOwnerOption.id)) {
        options.unshift(this.currentOwnerOption)
      }
      return options
    }
  },
  watch: {
    initialData: {
      immediate: true,
      handler () {
        this.syncInitialData()
      }
    }
  },
  methods: {
    handleParentChange (id) {
      this.currentParentOption = id ? this.displayedParentOptions.find(item => Number(item.id) === Number(id)) || null : null
    },
    openParentOptions (open) {
      if (open && !this.parentOptions.length) this.loadParentOptions()
    },
    searchParents (keyword) {
      window.clearTimeout(this.parentSearchTimer)
      this.parentRequestId += 1
      this.parentSearchTimer = window.setTimeout(() => {
        this.parentKeyword = String(keyword || '').trim()
        this.parentPage = 1
        this.loadParentOptions()
      }, 300)
    },
    changeParentPage (page) {
      this.parentPage = page
      this.loadParentOptions()
    },
    async loadParentOptions () {
      const requestId = ++this.parentRequestId
      this.parentLoading = true
      try {
        const res = await getAgentList({ page: this.parentPage, pageSize: 20, keyword: this.parentKeyword || undefined })
        if (requestId !== this.parentRequestId) return
        if (res.data.code !== 200) throw new Error(res.data.msg || '获取上级代理商失败')
        this.parentOptions = res.data.data.records || []
        this.parentTotal = Number(res.data.data.total) || 0
      } catch (err) {
        if (requestId !== this.parentRequestId) return
        this.parentOptions = []
        this.parentTotal = 0
        this.$Message.error(this.getErrorMessage(err, err.message || '获取上级代理商失败'))
      } finally {
        if (requestId === this.parentRequestId) this.parentLoading = false
      }
    },
    getErrorMessage (err, fallback) {
      return err && err.response && err.response.data && err.response.data.msg
        ? err.response.data.msg
        : fallback
    },
    syncInitialData () {
      this.currentParentOption = null
      const data = this.initialData || {}
      const form = {
        name: data.name || '',
        ownerUserId: data.ownerUserId || null,
        parentAgentId: data.parentAgentId || null,
        contactName: data.contactName || '',
        phone: data.phone || '',
        status: data.status === 0 ? 0 : 1
      }
      this.formValidate = { ...form }
      this.initialSnapshot = { ...form }
      const ownerOption = form.ownerUserId
        ? {
          id: form.ownerUserId,
          username: data.ownerUserName === '-' ? '' : data.ownerUserName,
          realName: data.ownerUserName === '-' ? '' : data.ownerUserName,
          phone: '',
          status: true
        }
        : null
      this.currentOwnerOption = ownerOption
      this.initialOwnerOption = ownerOption ? { ...ownerOption } : null
    },
    maskPhone (phone) {
      const value = String(phone || '')
      if (!value) return ''
      if (value.length <= 7) return `${value.slice(0, 2)}****${value.slice(-2)}`
      return `${value.slice(0, 3)}****${value.slice(-4)}`
    },
    getOwnerLabel (item) {
      return item.realName || item.username || `用户#${item.id}`
    },
    getOwnerSecondary (item) {
      const parts = []
      if (item.realName && item.username) parts.push(item.username)
      if (item.phone) parts.push(this.maskPhone(item.phone))
      return parts.join(' / ')
    },
    handleOwnerOpen (open) {
      if (open && !this.ownerOptions.length) this.loadOwnerOptions()
    },
    handleOwnerChange (ownerUserId) {
      if (!ownerUserId) {
        this.currentOwnerOption = null
        return
      }
      this.clearFieldError('ownerUserId')
      const option = this.displayedOwnerOptions.find(item => item.id === ownerUserId)
      if (option) this.currentOwnerOption = { ...option }
    },
    handleOwnerSearch (keyword) {
      window.clearTimeout(this.ownerSearchTimer)
      this.ownerSearchTimer = window.setTimeout(() => {
        this.ownerKeyword = String(keyword || '').trim()
        this.ownerPage = 1
        this.ownerSearched = Boolean(this.ownerKeyword)
        this.loadOwnerOptions()
      }, 400)
    },
    handleOwnerQueryChange (keyword) {
      if (String(keyword || '').trim()) return
      window.clearTimeout(this.ownerSearchTimer)
      const shouldReload = Boolean(this.ownerKeyword) || this.ownerPage !== 1
      this.ownerKeyword = ''
      this.ownerPage = 1
      this.ownerSearched = false
      if (shouldReload) this.loadOwnerOptions()
    },
    handleOwnerPageChange (page) {
      this.ownerPage = page
      this.loadOwnerOptions()
    },
    async loadOwnerOptions () {
      const requestId = ++this.ownerRequestId
      this.ownerLoading = true
      try {
        const res = await getAgentOwnerOptions({
          page: this.ownerPage,
          pageSize: this.ownerPageSize,
          keyword: this.ownerKeyword || undefined
        })
        if (requestId !== this.ownerRequestId) return
        if (res.data.code === 200) {
          const data = res.data.data || {}
          this.ownerOptions = data.records || []
          this.ownerTotal = Number(data.total) || 0
        } else {
          this.ownerOptions = []
          this.ownerTotal = 0
          this.$Message.error(res.data.msg || '获取负责人账号失败')
        }
      } catch (err) {
        if (requestId !== this.ownerRequestId) return
        this.ownerOptions = []
        this.ownerTotal = 0
        this.$Message.error(this.getErrorMessage(err, '获取负责人账号失败'))
      } finally {
        if (requestId === this.ownerRequestId) this.ownerLoading = false
      }
    },
    handleSubmit () {
      if (this.submitting) return
      this.clearFieldErrors()
      this.$refs.formValidate.validate(valid => {
        if (!valid) {
          this.focusFirstInvalidField()
          this.$Message.error('请先完善表单')
          return
        }
        try {
          this.$emit('on-ok', buildAgentPayload(this.formValidate))
        } catch (err) {
          this.$Message.error(err.message || '请先完善表单')
        }
      })
    },
    handleReset () {
      window.clearTimeout(this.parentSearchTimer)
      this.parentRequestId += 1
      this.parentLoading = false
      this.currentParentOption = null
      this.parentKeyword = ''
      this.parentPage = 1
      window.clearTimeout(this.ownerSearchTimer)
      this.clearFieldErrors()
      this.$refs.formValidate.resetFields()
      this.$nextTick(() => {
        this.formValidate = { ...this.initialSnapshot }
        this.currentOwnerOption = this.initialOwnerOption
          ? { ...this.initialOwnerOption }
          : null
        this.ownerKeyword = ''
        this.ownerPage = 1
        this.ownerSearched = false
        this.loadOwnerOptions()
      })
    },
    clearFieldError (field) {
      if (this.fieldErrors[field]) this.$set(this.fieldErrors, field, '')
    },
    clearFieldErrors () {
      this.fieldErrors = {
        name: '',
        ownerUserId: ''
      }
    },
    focusOwnerSelect () {
      const select = this.$refs.ownerSelect
      if (select && select.$refs.reference) select.$refs.reference.focus()
    },
    focusFirstInvalidField () {
      if (!String(this.formValidate.name || '').trim()) {
        if (this.$refs.nameInput) this.$refs.nameInput.focus()
        return
      }
      if (!this.formValidate.ownerUserId) this.focusOwnerSelect()
      else if (String(this.formValidate.phone || '').trim() && this.$refs.phoneInput) {
        this.$refs.phoneInput.focus()
      }
    },
    setFieldError (field, message) {
      this.$set(this.fieldErrors, field, '')
      this.$nextTick(() => this.$set(this.fieldErrors, field, message))
    },
    handleServerError (message) {
      if (message.indexOf('代理商名称') !== -1) {
        this.setFieldError('name', message)
        this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus())
      }
      if (message.indexOf('账号不存在') !== -1 || message.indexOf('已绑定其他代理商') !== -1) {
        this.formValidate.ownerUserId = null
        this.currentOwnerOption = null
        this.setFieldError('ownerUserId', message)
        this.$nextTick(() => {
          this.focusOwnerSelect()
        })
      }
    }
  },
  mounted () {
    this.loadOwnerOptions()
  },
  beforeDestroy () {
    window.clearTimeout(this.parentSearchTimer)
    this.parentRequestId += 1
    window.clearTimeout(this.ownerSearchTimer)
    this.ownerRequestId += 1
  }
}
</script>

<style lang="less" scoped>
.agent-form {
  .owner-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 30px;
  }

  .owner-primary {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .owner-secondary {
    flex: none;
    color: #808695;
    font-size: 12px;
  }

  .owner-secondary em {
    margin-left: 8px;
    color: #ed4014;
    font-style: normal;
  }

  .owner-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 8px;
    color: #808695;
    font-size: 12px;
  }

  .owner-empty {
    margin-top: 8px;
    margin-bottom: 0;
  }

  .form-actions {
    margin-bottom: 0;
    text-align: right;
  }

  .form-actions .ivu-btn + .ivu-btn {
    margin-left: 8px;
  }
}
</style>
